/** @jsxImportSource @emotion/react */
import { Input, InputGroup, InputRightAddon } from "@chakra-ui/input";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { createRef, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { FaInfo } from "react-icons/fa";
import { IoMdWarning } from "react-icons/io";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { CAPTCHA_PUBLIC_KEY, SERVER_URL } from "../../../config";
import { customTooltip, Flex } from "../../../styles/general";
import { appendGTMHeader, removeGTMScript } from "../../../utils/GoogleGTM";
import { validationEmail } from "../../../utils/Validation";
import { formatArchiveName } from "../../Settings/General";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import { IFormSignup, IFormSignupValidation, SignupFormSection } from "../Form";
import { EMAIL_PROVIDER_DOMAINS } from "../../../utils/emailProviderDomains";

const FormContainer = styled.div`
  width: 560px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-top: 48px;
  > div + div {
    margin-top: 24px;
  }
`;

const FormWrapper = styled.div<{ hasError?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .label {
    font-size: 16px;
    font-weight: 600;
    color: #222;
    margin-bottom: 8px;
  }

  .single-input-wrapper {
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: start;

    grid-gap: 16px;
  }

  .ekp3u414 {
    width: 80px;
    height: 40px;
    margin-left: 10px;
  }

  .envmntf9 {
    margin: 16px 0 0 0;
    grid-gap: 16px;

    > div {
      margin: 0;
    }
  }

  .react-tel-input .form-control {
    width: 100%;
    height: 40px;
    border: 1px solid #dddddd;
    border-radius: 5px;
    outline: none;
    font-size: 15px;
    color: #383838;

    ${(props) =>
      props.hasError &&
      css`
        border: 1px solid #c41700;
        box-shadow: 0px 0px 6px #c417004d;
      `}

    :focus {
      box-shadow: 0px 0px 6px #fb972e4d;
      border: 1px solid #fb972e;
    }
  }

  .react-tel-input .selected-flag:focus:before,
  .react-tel-input .selected-flag.open:before {
    box-shadow: none;
    border: none;
    outline: none;
  }

  .react-tel-input .country-list {
    width: 360px;
  }

  > div:last-of-type {
    width: 100%;
  }
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  margin-top: 45px !important;
  display: flex;
  align-items: center;
  justify-content: center;

  > button {
    width: 100%;
  }

  > button + button {
    margin-left: 16px;
  }
`;

const InputCSS = css`
  width: 100%;
  height: 40px;
  border: 1px solid #dddddd;
  border-radius: 5px 0 0 5px;
  outline: none;
  font-size: 15px;
  padding: 10px;
  color: #383838;
  opacity: 1;
  -webkit-transition: box-shadow 0.3s, border 0.3s;
  transition: box-shadow 0.3s, border 0.3s;
  background-color: #ffffff;
  text-align: right;

  &::placeholder {
    font-size: 14px;
    letter-spacing: 0.16px;
    color: #979797;
  }
`;

const AddonInputCSS = css`
  width: 100%;
  height: 40px;
  border: 1px solid #dddddd;
  border-radius: 0 5px 5px 0;
  outline: none;
  font-size: 15px;
  padding: 10px;
  color: #383838;
  opacity: 1;
  -webkit-transition: box-shadow 0.3s, border 0.3s;
  transition: box-shadow 0.3s, border 0.3s;
  text-align: left;
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

interface CountryData {
  name: string;
  dialCode: string;
  countryCode: string;
  format: string;
}

interface SecondFormProps {
  setSelectedForm: React.Dispatch<React.SetStateAction<SignupFormSection>>;
  form: IFormSignup;
  handleChangeForm: (
    key: string,
    value: string,
    secondKey?: string,
    secondValue?: string
  ) => void;
  formValidation: IFormSignupValidation;
  handleChangeFormValidation: (key: string, value: boolean) => void;
}

export default function SecondForm({
  setSelectedForm,
  form,
  handleChangeForm,
  formValidation,
  handleChangeFormValidation,
}: SecondFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isCaptchaReady, setIsCaptchaReady] = useState(false);
  const [captchaValue, setCaptchaValue] = useState("");

  const recaptchaRef = createRef();

  function onChange(value) {
    setIsCaptchaReady(true);
    setCaptchaValue(value);
  }

  useEffect(() => {
    const sc = appendGTMHeader();

    return () => {
      removeGTMScript(sc);
    };
  }, []);

  async function handleClick() {
    setIsLoading(true);

    const appNameExists = await checkIfAppNameExists(form.appName);
    if (appNameExists) {
      handleChangeFormValidation("isAppNameValid", false);
      toast("App Name already exists", { type: "error" });
      setIsLoading(false);
      return;
    }

    const domain = (form.domainSuffix + "@" + form.domain)
      .trim()
      .toLocaleLowerCase();

    const hasInvalidDomain = EMAIL_PROVIDER_DOMAINS.some(
      (d) => form.domain.trim() === d
    );

    if (hasInvalidDomain) {
      toast("Invalid domain", { type: "error" });
      handleChangeFormValidation("isDomainValid", false);
      setIsLoading(false);
      return;
    }

    if (!validationEmail(domain)) {
      toast("Incorrect domain format", { type: "error" });
      handleChangeFormValidation("isDomainValid", false);

      setIsLoading(false);
      return;
    }

    if (form.phone.length < 6) {
      toast("Incorrect phone format", { type: "error" });
      handleChangeFormValidation("isPhoneValid", false);
      setIsLoading(false);
      return;
    }

    if (form.sendFromName.trim().length === 0) {
      toast("Send From Name is invalid", { type: "error" });
      setIsLoading(false);

      return;
    }

    try {
      const { phone } = form;
      const phoneExists = await checkIfPhoneExists(phone);

      if (phoneExists) {
        handleChangeFormValidation("isPhoneValid", false);
        toast("Phone already exists", { type: "error" });
        return;
      }

      const response = await fetch(`${SERVER_URL}/auth/check_user_account`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-type": "application/json",
        },
        body: JSON.stringify({
          phone,
          captchaResponse: captchaValue,
        }),
      });

      if (!response.ok) {
        const responseJSON = await response.json();

        toast(responseJSON.error.message, { type: "error" });
        return;
      }
      const responseJSON = await response.json();

      handleChangeForm("origin", responseJSON.data?.origin);

      setSelectedForm(
        responseJSON.data?.origin === "LEARNISTIC"
          ? SignupFormSection.EnterPasscodeLearnistic
          : SignupFormSection.TemporyPasscode
      );
    } catch (error) {
      toast("Unexpected error", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  async function checkIfAppNameExists(appName: string) {
    const response: Response = await fetch(
      `${SERVER_URL}/auth/check_app_name/${appName}`
    );
    if (response.status === 200) {
      const responseObject = await response.json();
      return responseObject.data.exists;
    } else {
      return false;
    }
  }

  async function checkIfPhoneExists(phone: string) {
    const response: Response = await fetch(
      `${SERVER_URL}/auth/check_phone/${phone}`
    );

    if (response.status === 200) {
      const responseObject = await response.json();
      return responseObject.data.exists;
    } else {
      return false;
    }
  }

  function isDisabled() {
    return (
      form.appName === "" ||
      form.phone === "" ||
      form.sendFromName.trim() === "" ||
      form.domainSuffix === "" ||
      form.domain === "" ||
      !formValidation.isAppNameValid ||
      !formValidation.isPhoneValid ||
      !isCaptchaReady
    );
  }

  return (
    <FormContainer>
      <FormWrapper>
        <div key={"app-name"} data-tip data-for={"app-name"}>
          <Flex>
            <div className="label">App Name</div>
            <div
              css={css`
                margin-bottom: 4px;
                margin-left: 4px;
              `}
            >
              <FaInfo width={14} height={14} size={14} />

              <ReactTooltip
                id={"app-name"}
                place="right"
                effect="solid"
                backgroundColor="#333333"
                css={customTooltip}
              >
                <TooltipText>
                  e.g. https://archive.berserkermail.com/APPNAME
                </TooltipText>
              </ReactTooltip>
            </div>
          </Flex>
        </div>

        {!formValidation.isAppNameValid && (
          <ReactTooltip
            id={"app-name_isvalid"}
            place="right"
            effect="solid"
            event=""
            backgroundColor="#333333"
            css={customTooltip}
          >
            <TooltipText>App Name already exists</TooltipText>
          </ReactTooltip>
        )}
        <StyledInput
          key={"app-name_isvalid"}
          data-tip
          data-for={"app-name_isvalid"}
          value={form.appName}
          onChange={(e) => {
            handleChangeForm("appName", formatArchiveName(e.target.value));
            handleChangeFormValidation("isAppNameValid", true);
          }}
          onBlur={async (e) => {
            const appNameExists = await checkIfAppNameExists(e.target.value);
            if (appNameExists) {
              handleChangeFormValidation("isAppNameValid", false);
              toast("App Name already exists", { type: "error" });
              return;
            }
          }}
          placeholder="Enter app name"
          css={css`
            border-color: ${formValidation.isAppNameValid ? "#dddddd" : "red"};
          `}
        />
        <div
          key={"app-name"}
          data-tip
          data-for={"app-name"}
          css={css`
            font-size: 14px;
            color: #707070;
            margin-top: 10px;
          `}
        >
          The app name is a unique short code for your BerserkerMail account. It
          is used for your archive page and other locations in the platform.
          Please keep it short.
        </div>
      </FormWrapper>

      <div className="flex-input">
        <FormWrapper hasError={!formValidation.isPhoneValid}>
          <div className="label">Phone Number</div>

          {!formValidation.isPhoneValid && (
            <ReactTooltip
              id={"phone_is_invalid"}
              place="right"
              effect="solid"
              event=""
              backgroundColor="#333333"
              css={customTooltip}
            >
              <TooltipText>Phone already exists</TooltipText>
            </ReactTooltip>
          )}
          <div
            css={css`
              width: 100%;
            `}
            key={"phone_is_invalid"}
            data-tip
            data-for={"phone_is_invalid"}
          >
            <PhoneInput
              country={"us"}
              value={form.phone}
              onBlur={async (e) => {
                const phoneExists = await checkIfPhoneExists(
                  e.target.value.replace(/\D/g, "")
                );

                if (phoneExists) {
                  handleChangeFormValidation("isPhoneValid", false);
                  toast("Phone already exists", { type: "error" });
                }
              }}
              onChange={(value, country: CountryData, event, form) => {
                handleChangeFormValidation("isPhoneValid", true);

                if (Object.keys(country).length > 0) {
                  handleChangeForm(
                    "phone",
                    value,
                    "countryCode",
                    country.dialCode
                  );
                } else {
                  handleChangeForm("phone", value);
                }
              }}
            />
          </div>

          <div
            css={css`
              font-size: 14px;
              color: #707070;
              margin-top: 10px;
              display: flex;
            `}
          >
            <div
              css={css`
                color: #edba11;
                margin-top: 2px;
                margin-right: 5px;
              `}
            >
              <IoMdWarning />
            </div>

            <div>
              <b>Please do not use a landline.</b> An SMS verification code will
              be sent to your mobile number.
            </div>
          </div>
        </FormWrapper>
      </div>

      <div className="flex-input">
        <FormWrapper>
          <div className="label">Send From Name</div>
          <StyledInput
            value={form.sendFromName}
            onChange={(e) => {
              handleChangeForm("sendFromName", e.target.value);
              handleChangeFormValidation("isSendFromNameValid", true);
            }}
            placeholder="Enter send from name"
            css={css`
              border-color: ${formValidation.isSendFromNameValid
                ? "#dddddd"
                : "red"};
            `}
          />
        </FormWrapper>

        <FormWrapper>
          <div className="label">Your Domain</div>
          <StyledInput
            value={form.domain}
            onChange={(e) => {
              handleChangeForm(
                "domain",
                e.target.value?.toLowerCase().split(" ").join("")
              );
              handleChangeFormValidation("isDomainValid", true);
            }}
            placeholder="Enter domain"
            css={css`
              border-color: ${formValidation.isDomainValid ? "#dddddd" : "red"};
            `}
          />
        </FormWrapper>
      </div>

      <FormWrapper>
        <div className="label">Send From Email</div>
        <InputGroup size="sm" display="grid" gridTemplateColumns="60% 40%">
          <Input
            _focus={{
              boxShadow: " 0px 0px 6px #fb972e4d",
              border: "1px solid #fb972e",
            }}
            placeholder="Enter send from email"
            onChange={(e) => {
              handleChangeForm(
                "domainSuffix",
                e.target.value.trim().split(" ").join("")
              );
              handleChangeFormValidation("isDomainValid", true);
            }}
            value={form.domainSuffix}
            css={InputCSS}
          />
          <InputRightAddon
            css={AddonInputCSS}
            children={form.domain ? `@${form.domain}` : "@example.com"}
          />
        </InputGroup>
      </FormWrapper>
      <ButtonsWrapper>
        <StyledButton
          onClick={() => setSelectedForm(SignupFormSection.Start)}
          inverted
        >
          Back
        </StyledButton>
        <StyledButton
          disable={isDisabled()}
          onClick={handleClick}
          isLoading={isLoading}
        >
          Next
        </StyledButton>
      </ButtonsWrapper>
      <div style={{ display: "flex", width: "100%", justifyContent: "right" }}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={CAPTCHA_PUBLIC_KEY}
          onChange={onChange}
        />
      </div>
    </FormContainer>
  );
}
